import { useContext, useEffect, useState } from 'react'
import { GlobalStyle } from '../../styles/global-styles'
import Button from '../../components/atoms/Button/button'
import { LoginContext } from '../../contexts/login-context'
import {
  PageContainer,
  ProfileForm,
  PageHeader,
  SubHeader,
  Form,
  ButtonContainer,
  FieldLabel,
  TextField
} from './profile-page-styles'
import DropDown from '../../components/atoms/DropDown/dropdown'
import { ApiContext } from '../../contexts/api-context'
import logAnalyticsEvent from '../../utilities/firebase-analytics'
import { ModalContext } from '../../contexts/modal-context'
import ResultPopup from '../../components/modal/popup/result-popup/result-popup'
import { getUserId } from '../../utilities/utils'
import { ListOption } from '../../types/andTypes'
import { useNavigate } from 'react-router-dom'
import ComboBox from '../../components/atoms/ComboBox/combobox'

const Profile = () => {
  const { user } = useContext(LoginContext)
  const { businessUnits, userProfile, apiClient } = useContext(ApiContext)
  const { setModalOpen, setModalContent, modalOpen } = useContext(ModalContext)
  const navigate = useNavigate()
  const [selectedBU, setSelectedBU] = useState<string>()
  const [selectedTeam, setSelectedTeam] = useState<string>()
  const [teamOptions, setTeamOptions] = useState<ListOption[]>([])

  const name = user?.displayName || ''
  const buOptions = businessUnits.filter(
    bU => bU.alternativeOffice !== true
  ) as unknown as ListOption[]

  logAnalyticsEvent('page_view', {
    page_location: '/profile',
    page_title: 'Profile',
    user: user?.email,
    team: userProfile?.team
  })

  useEffect(() => {
    setSelectedBU(userProfile?.businessUnit)
    setSelectedTeam(userProfile?.team)
  }, [userProfile])

  useEffect(() => {
    updateTeamOptions()
    if (selectedBU !== userProfile?.businessUnit) setSelectedTeam(undefined)
    else setSelectedTeam(userProfile?.team)
  }, [selectedBU])

  const updateUserProfile = async () => {
    const startTime = new Date().getTime()
    try {
      if (!user?.email || !selectedBU) return

      await apiClient.updateUserProfile({
        userId: getUserId(user.email),
        businessUnit: selectedBU,
        team: selectedTeam
      })

      logAnalyticsEvent('profile_update_success', {
        latency_ms: new Date().getTime() - startTime,
        user: user?.email,
        team: userProfile?.team
      })

      setModalContent(
        <ResultPopup
          headerText="Profile updated"
          success={true}
          actionButtonConfig={{
            text: 'Back to Home',
            isCircle: false,
            onClick: () => navigate('/')
          }}
        >
          <p>Your profile has been updated.</p>
        </ResultPopup>
      )
      setModalOpen(true)
    } catch {
      logAnalyticsEvent('profile_update_error', {
        latency_ms: new Date().getTime() - startTime,
        user: user?.email,
        team: userProfile?.team,
        page_title: 'Profile'
      })

      setModalContent(
        <ResultPopup headerText="Oops..Sorry" success={false}>
          <p>Something went wrong. Please try to update your profile again.</p>
        </ResultPopup>
      )
      setModalOpen(true)
    }
  }

  const updateTeamOptions = () => {
    const selectedBusinessUnit = businessUnits.find(bU => bU.id === selectedBU)
    const teams = selectedBusinessUnit?.teams || []

    setTeamOptions(teams as unknown as ListOption[])
  }

  const shouldDisableButton =
    !selectedBU || (selectedBU === userProfile?.businessUnit && selectedTeam === userProfile?.team)

  return (
    <>
      <GlobalStyle page="profile" />
      <PageContainer>
        <PageHeader>My Profile</PageHeader>
        <ProfileForm>
          <SubHeader>Personal Details</SubHeader>
          <Form>
            <FieldLabel>Name</FieldLabel>
            <TextField type="text" value={name} readOnly />
            <ComboBox
              listItems={buOptions}
              listValue={selectedBU}
              setListValue={setSelectedBU}
              disabled={modalOpen}
              valueProperty="id"
              displayTemplate="{name}, {location}"
              label="Select your default office location"
            />
            <DropDown
              listItems={teamOptions}
              listValue={selectedTeam}
              setListValue={setSelectedTeam}
              valueProperty="id"
              displayProperty="name"
              label="Select your Team"
            />
          </Form>
          <ButtonContainer>
            <Button
              disable={shouldDisableButton}
              buttonText="Update Profile"
              onClick={updateUserProfile}
              maxContent
            />
          </ButtonContainer>
        </ProfileForm>
      </PageContainer>
    </>
  )
}

export default Profile
